import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Power, Server, Cpu, Activity, Settings2, User, Building2, Mail,
  Hash, KeyRound, RefreshCw, CheckCircle2, AlertTriangle, Copy, Save,
  PlayCircle, StopCircle, Shield, Terminal,
} from "lucide-react";
import { Topbar } from "@/components/dashboard/Topbar";
import { GlassPanel } from "@/components/dashboard/GlassPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { APIError, apiRequest, readUISettings, writeUISettings } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute("/system")({
  head: () => ({ meta: [{ title: "System · GENRL" }] }),
  component: SystemPage,
});

type DaemonState = "online" | "offline" | "starting" | "stopping";
type RuntimeHealthPayload = {
  hub?: boolean;
  spoke?: boolean;
  hub_base?: string;
  hub_id?: string;
  spoke_base?: string;
  daemon?: { active_mode?: string };
};

function StatusDot({ state }: { state: DaemonState }) {
  const color =
    state === "online" ? "var(--success)" :
    state === "offline" ? "var(--danger)" :
    "var(--warning)";
  return (
    <span className="relative flex h-2.5 w-2.5">
      {state === "online" && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: color }} />
      )}
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: color }} />
    </span>
  );
}

function DaemonCard({
  name, icon: Icon, state, base, onToggle, lastEvent,
}: {
  name: string; icon: typeof Server; state: DaemonState; base: string;
  onToggle: () => void; lastEvent: string;
}) {
  const online = state === "online";
  const busy = state === "starting" || state === "stopping";
  return (
    <GlassPanel className="p-5">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border hairline bg-soft">
            <Icon className="h-4.5 w-4.5 text-foreground" />
          </div>
          <div>
            <p className="font-display text-sm font-semibold">{name}</p>
            <p className="font-mono text-[11px] text-muted-foreground">{base}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-full border hairline bg-soft px-2.5 py-1">
          <StatusDot state={state} />
          <span className="text-[11px] font-medium capitalize">{state}</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg border hairline bg-soft p-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Runtime</p>
          <p className="mt-0.5 font-mono text-xs">{state}</p>
        </div>
        <div className="rounded-lg border hairline bg-soft p-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Endpoint</p>
          <p className="mt-0.5 truncate font-mono text-xs">{base || "—"}</p>
        </div>
        <div className="rounded-lg border hairline bg-soft p-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Health</p>
          <p className="mt-0.5 font-mono text-xs">{busy ? "transitioning" : (online ? "steady" : "offline")}</p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>Last: {lastEvent}</span>
      </div>

      <Button
        onClick={onToggle}
        disabled={busy}
        className={cn(
          "mt-4 w-full gap-2",
          online ? "bg-[var(--danger)]/10 text-[var(--danger)] hover:bg-[var(--danger)]/15" : "",
        )}
        variant={online ? "ghost" : "default"}
      >
        {busy ? (
          <><RefreshCw className="h-4 w-4 animate-spin" /> {state === "starting" ? "Starting…" : "Stopping…"}</>
        ) : online ? (
          <><StopCircle className="h-4 w-4" /> Turn Off {name}</>
        ) : (
          <><PlayCircle className="h-4 w-4" /> Turn On {name}</>
        )}
      </Button>
    </GlassPanel>
  );
}

function SystemPage() {
  const [hub, setHub] = useState<DaemonState>("offline");
  const [spoke, setSpoke] = useState<DaemonState>("offline");
  const [keepOn, setKeepOn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [runtimeMode, setRuntimeMode] = useState("");
  const [spokeBase, setSpokeBase] = useState("spoke://local");
  const [settings, setSettings] = useState(() => readUISettings());
  const [profile, setProfile] = useState({
    company: "",
    user: "",
    email: "",
    company_type: "hybrid" as "cloud" | "enterprise" | "hybrid",
    hub_id: "",
    client_id: "",
  });

  const ready = hub === "online" && spoke === "online";

  async function runLocal(command: string, timeoutSeconds = 120) {
    return apiRequest<{ output?: string }>("/admin/v1/local/exec", {
      method: "POST",
      body: { command, timeout_seconds: timeoutSeconds },
      timeoutMs: Math.max(10000, timeoutSeconds * 1000 + 3000),
    });
  }

  function applyRuntime(payload: RuntimeHealthPayload) {
    setHub(payload.hub ? "online" : "offline");
    setSpoke(payload.spoke ? "online" : "offline");
    setSpokeBase(String(payload.spoke_base || "").trim() || "spoke://local");
    setRuntimeMode(String(payload.daemon?.active_mode || "").trim().toLowerCase());
    setSettings((prev) => ({
      ...prev,
      hub_base: String(payload.hub_base || prev.hub_base || "").trim(),
    }));
    setProfile((prev) => ({
      ...prev,
      hub_id: String(payload.hub_id || prev.hub_id || "").trim(),
      client_id: prev.client_id || String(payload.hub_id || "").trim(),
    }));
  }

  async function refreshRuntime() {
    const payload = await apiRequest<RuntimeHealthPayload>(
      "/api/v1/runtime/health",
      { method: "GET", timeoutMs: 8000 },
    );
    applyRuntime(payload);
  }

  async function refreshProfile() {
    const identity = await apiRequest<{ company_name?: string; user_name?: string; user_contact?: string; company_type?: string }>(
      "/admin/v1/spoke/profile/identity",
      { method: "GET", timeoutMs: 8000 },
    );
    setProfile((prev) => ({
      ...prev,
      company: String(identity.company_name || prev.company || "").trim(),
      user: String(identity.user_name || prev.user || "").trim(),
      email: String(identity.user_contact || prev.email || "").trim(),
      company_type: (["cloud", "enterprise", "hybrid"].includes(String(identity.company_type || "").toLowerCase())
        ? String(identity.company_type).toLowerCase()
        : prev.company_type) as "cloud" | "enterprise" | "hybrid",
      client_id: prev.client_id || String(identity.company_name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_"),
    }));
  }

  async function refreshLogs() {
    const payload = await apiRequest<{ lines?: string[] }>("/admin/v1/local/logs?source=hub&tail=240", {
      method: "GET",
      timeoutMs: 10000,
    });
    setLogs(Array.isArray(payload.lines) ? payload.lines : []);
  }

  async function refreshKeepOn() {
    try {
      const result = await runLocal("local_ui_watch.sh status", 30);
      const out = String(result.output || "").toLowerCase();
      setKeepOn(out.includes("ui-watch running"));
    } catch {
      // keep previous state
    }
  }

  async function refreshAll() {
    setLoading(true);
    try {
      await Promise.all([refreshRuntime(), refreshProfile(), refreshLogs(), refreshKeepOn()]);
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to refresh system state");
      else toast.error("Failed to refresh system state");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refreshAll();
  }, []);

  async function toggleDaemon(target: "hub" | "spoke") {
    const current = target === "hub" ? hub : spoke;
    const setState = target === "hub" ? setHub : setSpoke;
    const sharedRuntime = runtimeMode === "hub_and_spoke";
    if (sharedRuntime) {
      if (current === "online") {
        toast.info("Hub and Spoke are managed together in desktop mode. Close the daemon app to stop them.");
        return;
      }
      try {
        const payload = await apiRequest<RuntimeHealthPayload>("/api/v1/runtime/health", {
          method: "GET",
          timeoutMs: 8000,
        });
        applyRuntime(payload);
        const onlineNow = target === "hub" ? !!payload.hub : !!payload.spoke;
        if (onlineNow) toast.success(`${target === "hub" ? "Hub" : "Spoke"} is online`);
        else toast.error(`${target === "hub" ? "Hub" : "Spoke"} is not active. Restart daemon and try again.`);
      } catch (err) {
        if (err instanceof APIError) toast.error(err.detail || "Failed to refresh daemon runtime");
        else toast.error("Failed to refresh daemon runtime");
      }
      return;
    }
    const starting = current === "online" ? "stopping" : "starting";
    setState(starting);
    const command = target === "hub"
      ? (current === "online" ? "local_stop_hub.sh" : "local_load_hub.sh")
      : (current === "online" ? "local_stop_spoke.sh" : "local_load_spoke.sh");
    try {
      await runLocal(command, current === "online" ? 90 : 220);
      toast.success(`${target === "hub" ? "Hub" : "Spoke"} ${current === "online" ? "stopped" : "started"}`);
      await refreshAll();
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Runtime action failed");
      else toast.error("Runtime action failed");
      await refreshRuntime().catch(() => undefined);
    }
  }

  async function setWatchdog(next: boolean) {
    setKeepOn(next);
    try {
      await runLocal(`local_ui_watch.sh ${next ? "start" : "stop"}`, 45);
      toast.success(`Watchdog ${next ? "enabled" : "disabled"}`);
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to update watchdog");
      else toast.error("Failed to update watchdog");
      await refreshKeepOn();
    }
  }

  function saveSettings() {
    writeUISettings(settings);
    toast.success("Settings saved");
  }

  async function saveProfile() {
    try {
      await apiRequest("/admin/v1/spoke/profile/identity", {
        method: "PUT",
        body: {
          company_name: profile.company,
          user_name: profile.user,
          user_contact: profile.email,
          company_type: profile.company_type,
        },
        timeoutMs: 12000,
      });
      toast.success("Profile saved");
      await refreshProfile();
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to save profile");
      else toast.error("Failed to save profile");
    }
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  }

  const logText = logs.length ? logs.join("\n") : "No logs returned by daemon.";

  return (
    <>
      <Topbar title="System" subtitle="Daemon control, settings, and identity." />
      <main className="flex-1 px-6 py-6">
        <GlassPanel className="mb-6 flex flex-wrap items-center justify-between gap-4 p-5">
          <div className="flex items-center gap-3">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-xl shadow-[var(--shadow-glow)]"
              style={{ background: ready ? "var(--gradient-primary)" : "var(--soft)" }}
            >
              {ready ? <CheckCircle2 className="h-5 w-5 text-white" /> : <AlertTriangle className="h-5 w-5 text-[var(--warning)]" />}
            </div>
            <div>
              <p className="font-display text-base font-semibold">{ready ? "Ready to run jobs" : "Runtime not ready"}</p>
              <p className="text-xs text-muted-foreground">
                Hub {hub} · Spoke {spoke} · Mode {runtimeMode || "unknown"} · Keep-on {keepOn ? "true" : "false"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border hairline bg-soft px-4 py-2.5">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <div className="text-xs">
              <p className="font-medium">Watchdog</p>
              <p className="text-[10px] text-muted-foreground">ui-watch {keepOn ? "running" : "stopped"}</p>
            </div>
            <Switch checked={keepOn} onCheckedChange={(v) => { void setWatchdog(v); }} />
          </div>
        </GlassPanel>

        <Tabs defaultValue="daemon">
          <TabsList className="bg-soft">
            <TabsTrigger value="daemon"><Power className="mr-1.5 h-3.5 w-3.5" /> Daemon</TabsTrigger>
            <TabsTrigger value="settings"><Settings2 className="mr-1.5 h-3.5 w-3.5" /> Settings</TabsTrigger>
            <TabsTrigger value="profile"><User className="mr-1.5 h-3.5 w-3.5" /> Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="daemon" className="mt-4 space-y-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <DaemonCard
                name="Hub"
                icon={Server}
                state={hub}
                base={settings.hub_base || "http://127.0.0.1:8080"}
                onToggle={() => { void toggleDaemon("hub"); }}
                lastEvent={logs.find((line) => line.toLowerCase().includes("hub")) || "No recent hub event"}
              />
              <DaemonCard
                name="Spoke"
                icon={Cpu}
                state={spoke}
                base={spokeBase}
                onToggle={() => { void toggleDaemon("spoke"); }}
                lastEvent={logs.find((line) => line.toLowerCase().includes("spoke")) || "No recent spoke event"}
              />
            </div>

            <GlassPanel className="p-5">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Terminal className="h-4 w-4 text-muted-foreground" />
                  <p className="font-display text-sm font-semibold">Activity log</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs" onClick={() => { void refreshLogs(); }}>
                    <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs" onClick={() => { void copyText(logText); }}>
                    <Copy className="h-3 w-3" /> Copy
                  </Button>
                </div>
              </div>
              <pre className="max-h-56 overflow-auto rounded-lg border hairline bg-soft p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
{logText}
              </pre>
            </GlassPanel>
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <GlassPanel className="p-6">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <p className="font-display text-base font-semibold">API & runtime settings</p>
                  <p className="text-xs text-muted-foreground">Used for every UI API call to the Hub.</p>
                </div>
                <Button size="sm" className="gap-2" onClick={saveSettings}><Save className="h-3.5 w-3.5" /> Save</Button>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Hub API Base</Label>
                  <Input value={settings.hub_base} onChange={(e) => setSettings({ ...settings, hub_base: e.target.value })} className="font-mono text-xs" />
                  <p className="text-[10px] text-muted-foreground">Base URL for Hub API calls.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Admin API Key</Label>
                  <div className="relative">
                    <KeyRound className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={settings.api_key} onChange={(e) => setSettings({ ...settings, api_key: e.target.value })} className="pl-8 font-mono text-xs" />
                  </div>
                  <p className="text-[10px] text-muted-foreground">Sent as <code className="font-mono">X-API-Key</code>.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tenant ID</Label>
                  <Input value={settings.tenant_id} onChange={(e) => setSettings({ ...settings, tenant_id: e.target.value })} className="font-mono text-xs" />
                  <p className="text-[10px] text-muted-foreground">Sent as <code className="font-mono">X-Tenant-Id</code>.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Hugging Face Token</Label>
                  <Input value={settings.hf_token} onChange={(e) => setSettings({ ...settings, hf_token: e.target.value })} className="font-mono text-xs" />
                  <p className="text-[10px] text-muted-foreground">For Hugging Face import flows.</p>
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-xs">Auto-refresh interval ({settings.refresh_sec}s)</Label>
                  <input
                    type="range"
                    min={3}
                    max={60}
                    value={settings.refresh_sec}
                    onChange={(e) => setSettings({ ...settings, refresh_sec: Number(e.target.value) })}
                    className="w-full accent-[var(--primary)]"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground"><span>3s</span><span>60s</span></div>
                </div>
              </div>
            </GlassPanel>
          </TabsContent>

          <TabsContent value="profile" className="mt-4">
            <div className="grid gap-4 lg:grid-cols-3">
              <GlassPanel className="p-6 lg:col-span-2">
                <div className="mb-5 flex items-center justify-between">
                  <div>
                    <p className="font-display text-base font-semibold">Profile</p>
                    <p className="text-xs text-muted-foreground">Company and operator identity.</p>
                  </div>
                  <Button size="sm" className="gap-2" onClick={() => { void saveProfile(); }}><Save className="h-3.5 w-3.5" /> Save</Button>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Company name</Label>
                    <div className="relative">
                      <Building2 className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} className="pl-8" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">User name</Label>
                    <div className="relative">
                      <User className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input value={profile.user} onChange={(e) => setProfile({ ...profile, user: e.target.value })} className="pl-8" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Email</Label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="pl-8" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Company type</Label>
                    <div className="grid grid-cols-3 gap-1.5 rounded-lg border hairline bg-soft p-1">
                      {(["cloud", "enterprise", "hybrid"] as const).map((t) => (
                        <button
                          key={t}
                          onClick={() => setProfile({ ...profile, company_type: t })}
                          className={cn(
                            "rounded-md py-1.5 text-xs font-medium capitalize transition",
                            profile.company_type === t ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </GlassPanel>

              <GlassPanel className="p-6">
                <div className="mb-4 flex items-center gap-2">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                  <p className="font-display text-sm font-semibold">System IDs</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Hub ID (daemon-linked)</p>
                    <div className="mt-1 flex items-center justify-between rounded-lg border hairline bg-soft px-3 py-2">
                      <code className="font-mono text-xs">{profile.hub_id || "—"}</code>
                      <button className="text-muted-foreground hover:text-foreground" onClick={() => { void copyText(profile.hub_id || ""); }}><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Client ID</p>
                    <div className="mt-1 flex items-center justify-between rounded-lg border hairline bg-soft px-3 py-2">
                      <code className="font-mono text-xs">{profile.client_id || "—"}</code>
                      <button className="text-muted-foreground hover:text-foreground" onClick={() => { void copyText(profile.client_id || ""); }}><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  <div className="rounded-lg border hairline bg-soft p-3 text-[11px] text-muted-foreground">
                    <span className="font-mono">{profile.hub_id || "—"}</span> · <span className="font-mono">{profile.client_id || "—"}</span>
                  </div>
                  <Button size="sm" variant="outline" className="w-full gap-2" onClick={() => { void refreshAll(); }}>
                    <Activity className="h-3.5 w-3.5" /> Re-link to daemon
                  </Button>
                </div>
              </GlassPanel>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </>
  );
}
